import type { DataRouter, RouterState } from "react-router-dom";

import type { Models } from "api/types";
import { publicStorefrontPaths } from "modules/storefront/publicStorefrontRoute";
import { getProductTitle } from "services/HelperService";
import { getPromotionCardTitle } from "utils/promotionPresentation";

import { reportPageView, reportProductView } from "./appClientActivity";

const PRODUCT_LISTING_ID_PROPERTY = "retailProductListingId";
const PACKAGE_LISTING_ID_PROPERTY = "packageListingId";
const MEMBERSHIP_ID_PROPERTY = "membershipId";
const NAME_PROPERTY = "name";
const PAGE_PROPERTY = "page";
/** The backend shortens every property value to this length, so the page name is cut to fit. */
const MAX_PAGE_NAME_LENGTH = 40;
const CUSTOM_PLAN_ROUTE = "/custom-plan/:id";

interface ProductViewShape {
  idProperty: string;
  /** Where the page's loader data keeps the item name, which the PDP loader already fetched. */
  namePath: readonly string[];
}

const PRODUCT_SHAPE: ProductViewShape = {
  idProperty: PRODUCT_LISTING_ID_PROPERTY,
  namePath: ["listing", "name", "text"],
};
const PACKAGE_SHAPE: ProductViewShape = {
  idProperty: PACKAGE_LISTING_ID_PROPERTY,
  namePath: ["listing", "title"],
};
const MEMBERSHIP_SHAPE: ProductViewShape = {
  idProperty: MEMBERSHIP_ID_PROPERTY,
  namePath: ["membershipListing", "name"],
};

const PRODUCT_VIEW_SHAPE_BY_ROUTE: Record<string, ProductViewShape> = {
  [publicStorefrontPaths.product]: PRODUCT_SHAPE,
  [publicStorefrontPaths.productWithSource]: PRODUCT_SHAPE,
  [publicStorefrontPaths.standaloneProduct]: PRODUCT_SHAPE,
  [`${publicStorefrontPaths.standaloneProduct}/:source`]: PRODUCT_SHAPE,
  [publicStorefrontPaths.package]: PACKAGE_SHAPE,
  [CUSTOM_PLAN_ROUTE]: PACKAGE_SHAPE,
  [publicStorefrontPaths.membership]: MEMBERSHIP_SHAPE,
  [publicStorefrontPaths.membershipWithSource]: MEMBERSHIP_SHAPE,
};

/**
 * The route template a set of matches resolves to (`/pdp/product/:id`), joined from the matched
 * routes' own path patterns so no concrete id or other URL value ever reaches the report. Empty
 * when nothing with a path matched.
 */
export function routeTemplateFromMatches(matches: readonly { route: { path?: string } }[]): string {
  let template = "";
  for (const { route } of matches) {
    const segment = route.path;
    if (!segment) continue;
    template = segment.startsWith("/") ? segment : `${template.replace(/\/$/, "")}/${segment}`;
  }
  return template;
}

type RouteParams = Readonly<Record<string, string | undefined>>;

function valueAtPath(data: unknown, path: readonly string[]): unknown {
  let cursor = data;
  for (const key of path) {
    if (cursor === null || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

function stringAtPath(data: unknown, path: readonly string[]): string | undefined {
  const value = valueAtPath(data, path);
  return typeof value === "string" && value !== "" ? value : undefined;
}

function nonEmpty(value: string | null | undefined): string | undefined {
  return value === null || value === undefined || value === "" ? undefined : value;
}

/** The name of the list entry whose `idKey` equals the route param, the way the page picks its item. */
function nameOfListEntry(
  loaderData: unknown,
  listPath: readonly string[],
  idKey: string,
  id: string | undefined,
  namePath: readonly string[],
): string | undefined {
  const list = valueAtPath(loaderData, listPath);
  if (!Array.isArray(list) || id === undefined) return undefined;
  return stringAtPath(
    list.find((entry) => stringAtPath(entry, [idKey]) === id),
    namePath,
  );
}

type ItemNameResolver = (loaderData: unknown, params: RouteParams) => string | undefined;

/** Treatment Details heads the page with the product and variant name joined, so the report does too. */
const treatmentName: ItemNameResolver = (loaderData) =>
  nonEmpty(
    getProductTitle(
      stringAtPath(loaderData, ["platformUserPackage", "productName"]),
      stringAtPath(loaderData, ["platformUserPackage", "variantName"]),
    ),
  );

const earnedProductName: ItemNameResolver = (loaderData, params) =>
  nameOfListEntry(loaderData, ["earnedProducts"], "packageId", params.packageId, ["name", "text"]);

const walletOfferName: ItemNameResolver = (loaderData) => {
  const promotion = valueAtPath(loaderData, ["promotion"]);
  if (promotion === null || typeof promotion !== "object") return undefined;
  return nonEmpty(getPromotionCardTitle(promotion as Models<"PromotionModel">));
};

const rewardName: ItemNameResolver = (loaderData) => stringAtPath(loaderData, ["reward", "title"]);

const membershipName: ItemNameResolver = (loaderData, params) =>
  nameOfListEntry(
    loaderData,
    ["userMemberships"],
    "platformUserMembershipId",
    params.platformUserMembershipId,
    ["membership", "name"],
  );

const MEMBERSHIP_NAME_PATH_BY_LOADER_DATA = {
  cancelOffers: ["membership", "membership", "name"],
  change: ["changeOptions", "currentMembership", "name"],
  dunning: ["platformUserMembership", "membership", "name"],
  paymentMethod: ["membership", "membership", "name"],
} as const;

const membershipNameAt =
  (path: readonly string[]): ItemNameResolver =>
  (loaderData) =>
    stringAtPath(loaderData, path);

const GIFT_CARD_LIST_PATHS = [["sentGiftCards"], ["receivedGiftCards"]] as const;

/**
 * The other party on a gift card, the way the page picks the name to prefill when buying another:
 * the sender of a received card, the recipient of a sent one. A card bought for oneself has no
 * other party.
 */
const giftCardCounterpartName: ItemNameResolver = (loaderData, params) => {
  const id = params.giftCardTransferId;
  if (id === undefined) return undefined;
  for (const listPath of GIFT_CARD_LIST_PATHS) {
    const list = valueAtPath(loaderData, listPath);
    const card = Array.isArray(list)
      ? list.find((entry) => stringAtPath(entry, ["giftCardTransferItemId", "value"]) === id)
      : undefined;
    if (card === undefined) continue;
    const ownershipType = stringAtPath(card, ["ownershipType"]);
    if (ownershipType === "Myself") return undefined;
    return stringAtPath(card, [ownershipType === "Receiver" ? "senderName" : "recipientName"]);
  }
  return undefined;
};

/**
 * Detail pages that show one named item. The name is what the page itself heads or describes the
 * item with, read from the loader data already in router state: a gift card's other party, or the
 * membership a manage flow acts on.
 */
const ITEM_NAME_RESOLVER_BY_ROUTE: Record<string, ItemNameResolver> = {
  "/wallet/treatments/:packageId": treatmentName,
  "/wallet/products/earned/:packageId": earnedProductName,
  "/wallet/offers/:offerId": walletOfferName,
  "/rewards/:rewardId": rewardName,
  "/wallet/giftcards/:giftCardTransferId": giftCardCounterpartName,
  "/wallet/memberships/manage/:platformUserMembershipId": membershipName,
  "/wallet/memberships/manage/:platformUserMembershipId/cancel": membershipName,
  "/wallet/memberships/manage/:platformUserMembershipId/freeze": membershipName,
  "/wallet/memberships/manage/:platformUserMembershipId/cancel-offers": membershipNameAt(
    MEMBERSHIP_NAME_PATH_BY_LOADER_DATA.cancelOffers,
  ),
  "/wallet/memberships/manage/:platformUserMembershipId/change": membershipNameAt(
    MEMBERSHIP_NAME_PATH_BY_LOADER_DATA.change,
  ),
  "/wallet/memberships/manage/:platformUserMembershipId/dunning": membershipNameAt(
    MEMBERSHIP_NAME_PATH_BY_LOADER_DATA.dunning,
  ),
  "/wallet/memberships/manage/:platformUserMembershipId/dunning/cancel": membershipNameAt(
    MEMBERSHIP_NAME_PATH_BY_LOADER_DATA.dunning,
  ),
  "/wallet/memberships/manage/:platformUserMembershipId/payment-method": membershipNameAt(
    MEMBERSHIP_NAME_PATH_BY_LOADER_DATA.paymentMethod,
  ),
};

/** The leaf-most route `handle.name`, the friendly page title the router table gives each route. */
function pageNameFromMatches(
  matches: readonly { route: { handle?: unknown } }[],
): string | undefined {
  for (const { route } of matches.toReversed()) {
    const name = stringAtPath(route.handle, ["name"])?.trim();
    if (name) return name.slice(0, MAX_PAGE_NAME_LENGTH).trimEnd();
  }
  return undefined;
}

function pageViewProperties(
  route: string,
  matches: readonly { route: { handle?: unknown } }[],
  params: RouteParams,
  loaderData: unknown,
): Record<string, string> | undefined {
  const page = pageNameFromMatches(matches);
  const name = ITEM_NAME_RESOLVER_BY_ROUTE[route]?.(loaderData, params);
  if (page === undefined && name === undefined) return undefined;
  return {
    ...(page !== undefined && { [PAGE_PROPERTY]: page }),
    ...(name !== undefined && { [NAME_PROPERTY]: name }),
  };
}

/**
 * The id from the URL plus the name the PDP loader already put in router state. A loader that
 * resolved without a listing (a location that does not sell it) leaves the name out; the view is
 * still reported by id.
 */
function productViewProperties(
  route: string,
  params: RouteParams,
  loaderData: unknown,
): Record<string, string> | undefined {
  const shape = PRODUCT_VIEW_SHAPE_BY_ROUTE[route];
  const id = params.id;
  if (shape === undefined || id === undefined) return undefined;
  const name = stringAtPath(loaderData, shape.namePath);
  return name === undefined
    ? { [shape.idProperty]: id }
    : { [shape.idProperty]: id, [NAME_PROPERTY]: name };
}

/**
 * Reports one page view per settled pathname, the same key the Segment page tracker uses, so a
 * search-param rewrite or a loader revalidation never reports twice. Subscribing to the router
 * keeps this out of React: no effect, and redirects never show up because only the final
 * location settles. A detail page that reports a product view is not also reported as a page
 * view: the product view already says what was viewed, and on which route.
 */
export function trackRouterPageViews(router: DataRouter): void {
  let lastPathname: string | undefined;

  const onRouterState = (state: RouterState) => {
    if (!state.initialized || state.navigation.state !== "idle") return;
    const { pathname } = state.location;
    if (pathname === lastPathname) return;
    lastPathname = pathname;

    const route = routeTemplateFromMatches(state.matches);
    if (route === "") return;

    const leaf = state.matches.at(-1);
    if (leaf === undefined) return;
    const loaderData: unknown = state.loaderData[leaf.route.id];

    const productProperties =
      state.errors === null ? productViewProperties(route, leaf.params, loaderData) : undefined;
    if (productProperties !== undefined) {
      reportProductView(route, productProperties);
      return;
    }
    reportPageView(route, pageViewProperties(route, state.matches, leaf.params, loaderData));
  };

  router.subscribe(onRouterState);
  onRouterState(router.state);
}
